interface ProjectTelemetryOptions {
  activate?: () => void | Promise<void>
  deactivate?: () => void | Promise<void>
  trackPageView?: () => void | Promise<void>
}

export function useProjectTelemetryPageTracking(
  options: ProjectTelemetryOptions,
) {
  const nuxtApp = useNuxtApp()
  const router = useRouter()
  const projectStore = useProjectStore()
  const { selectedProject, isConfidential } = storeToRefs(projectStore)

  let lastHandledPage = ''

  function isProjectScopedRoute(path: string) {
    return /^\/projects\/[^/]+(?:\/.*)?$/.test(path) && path !== '/projects/new'
  }

  function getRouteProjectId() {
    const routeId = router.currentRoute.value.params.id
    return Array.isArray(routeId) ? routeId[0] : routeId
  }

  function shouldBlockTelemetry() {
    const currentRoute = router.currentRoute.value

    if (currentRoute.path === '/projects/new' && isConfidential.value) {
      return true
    }

    if (!isProjectScopedRoute(currentRoute.path)) {
      return false
    }

    const routeProjectId = getRouteProjectId()
    if (!routeProjectId || selectedProject.value?.id !== routeProjectId) {
      return true
    }

    return Boolean(selectedProject.value.isConfidential)
  }

  async function syncTelemetry() {
    const currentPageKey = router.currentRoute.value.fullPath

    if (shouldBlockTelemetry()) {
      await options.deactivate?.()
      lastHandledPage = ''
      return
    }

    if (lastHandledPage === currentPageKey) {
      return
    }

    await options.activate?.()
    lastHandledPage = currentPageKey
    await options.trackPageView?.()
  }

  function scheduleSync() {
    nextTick(() => {
      void syncTelemetry()
    })
  }

  router.beforeEach((_to, from) => {
    const isLeavingConfidentialNewProject
      = from.path === '/projects/new' && isConfidential.value

    if (
      selectedProject.value?.isConfidential
      || isLeavingConfidentialNewProject
    ) {
      lastHandledPage = ''
      void options.deactivate?.()
    }
  })

  nuxtApp.hook('page:finish', scheduleSync)

  watch(
    () => [
      router.currentRoute.value.fullPath,
      isConfidential.value,
      selectedProject.value?.id,
      selectedProject.value?.isConfidential,
    ],
    scheduleSync,
    { immediate: true },
  )

  return {
    shouldBlockTelemetry,
  }
}
